import _ from 'lodash'
import {type DocumentManager, pointerUtils} from '@wix/document-manager-core'
import type {DalValue, Experiment} from '@wix/document-services-types'
import {ReportableWarning} from '@wix/document-manager-utils'
import type {PageMigrator} from '../dataMigrationRunner'

const {getPointer} = pointerUtils

const moveColor = (documentManager: DocumentManager, styleId: string, style: DalValue) => {
    const {backgroundColor} = style.cssProperties!
    const {dal, logger} = documentManager

    const fixedStyle = _.cloneDeep(style)
    delete fixedStyle.cssProperties.backgroundColor

    if (!fixedStyle.cssCustomProperties) {
        fixedStyle.cssCustomProperties = {}
    }
    fixedStyle.cssCustomProperties.backgroundColor = backgroundColor

    dal.set(getPointer(styleId, 'style'), fixedStyle)

    logger.captureError(
        new ReportableWarning({
            errorType: 'backgroundColorMoved',
            message: `Background style ${styleId}: backgroundColor moved from cssProperties to cssCustomProperties (${backgroundColor})`,
            extras: {
                styleId,
                cssProperties: style.cssProperties,
                cssCustomProperties: style.cssCustomProperties
            }
        })
    )
}

const skipColor = (documentManager: DocumentManager, styleId: string, style: DalValue) => {
    const {dal, logger} = documentManager

    const fixedStyle = _.cloneDeep(style)
    delete fixedStyle.cssProperties.backgroundColor

    dal.set(getPointer(styleId, 'style'), fixedStyle)

    logger.captureError(
        new ReportableWarning({
            errorType: 'backgroundColorSkipped',
            message: `Background style ${styleId}: backgroundColor deleted from cssProperties (already exists in cssCustomProperties)`,
            extras: {
                styleId,
                cssProperties: style.cssProperties,
                cssCustomProperties: style.cssCustomProperties
            }
        })
    )
}

const migratePage = (documentManager: DocumentManager, pageId: string, experimentInstance: Experiment) => {
    if (!experimentInstance.isOpen('dm_cleanManifestHiddenProperties')) {
        return
    }

    const {dal} = documentManager

    const styleItems = dal.query(
        'style',
        dal.queryFilterGetters.getPageCompFilter(pageId),
        (style: DalValue) => style?.type === 'wixSystemElements.Background.Style'
    )

    _.forEach(styleItems, (style: DalValue, styleId: string) => {
        const hasColorProperty = !_.isNil(style.cssProperties?.backgroundColor)
        if (!hasColorProperty) {
            return
        }

        const hasColorCustomProperty = !_.isNil(style.cssCustomProperties?.backgroundColor)
        if (hasColorCustomProperty) {
            skipColor(documentManager, styleId, style)
        } else {
            moveColor(documentManager, styleId, style)
        }
    })
}

const name = 'cleanManifestHiddenProperties'
const version = 0
const experimentalVersions = [{experiment: 'dm_cleanManifestHiddenProperties', version: 1}]

export const cleanManifestHiddenProperties: PageMigrator = {
    migratePage,
    name,
    version,
    experimentalVersions,
    fixerRequiresReruns: true
}
